import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode
} from 'react';
import { logError, handleApiError } from '../utils/errorHandler';
import { authApi } from '../services/api/endpoints/auth';
import { secureSetItem } from '../utils/security';
import logger from '../utils/logger';
import type { AuthUser, AuthContextType, Store } from '../types';

interface AuthProviderProps {
  children: ReactNode;
}

interface LoginResponse {
  success: boolean;
  error?: string;
}

interface RefreshResponse {
  success: boolean;
  error?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
  const [currentStore, setCurrentStore] = useState<Store | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);

  useEffect(() => {
    const savedToken = localStorage.getItem('token');
    const savedRefreshToken = localStorage.getItem('refreshToken');
    const savedUser = localStorage.getItem('user');
    const savedStore = localStorage.getItem('store');

    if (savedToken && savedRefreshToken && savedUser && savedStore) {
      try {
        const parsedUser = JSON.parse(savedUser);
        const parsedStore = JSON.parse(savedStore);

        setToken(savedToken);
        setRefreshToken(savedRefreshToken);
        setCurrentUser(parsedUser);
        setCurrentStore(parsedStore);

        // セッション復元をログ
        logger.userAction('session_restored', {
          userId: parsedUser.user_id || parsedUser.userId,
          userName: parsedUser.name,
          storeId: parsedStore.id,
          storeName: parsedStore.name,
          timestamp: new Date().toISOString()
        });
      } catch (error) {
        logger.error('Failed to restore auth state', {
          context: 'auth',
          error: error instanceof Error ? error.message : String(error)
        });
        // Clear invalid session data
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('store');
        localStorage.removeItem('authSession');
      }
    }
    setIsLoading(false);

    // Auto logout will be handled after logout function is defined
  }, []);

  const login = async (userId: string, password: string): Promise<LoginResponse> => {
    setError(null);
    setIsLoading(true);

    // ログイン試行をログ
    logger.userAction('login_attempt', {
      userId,
      timestamp: new Date().toISOString()
    });

    const startTime = Date.now();

    try {
      const data = await authApi.login(userId, password);

      // デバッグ用ログ
      console.log('Login API Response:', data);

      // アクセストークンの確認
      if (!data.accessToken || !data.refreshToken) {
        console.error('Invalid response structure:', Object.keys(data));
        throw new Error('Invalid response: missing tokens');
      }

      setCurrentUser(data.user);
      setCurrentStore(data.store);
      setToken(data.accessToken);
      setRefreshToken(data.refreshToken);

      localStorage.setItem('token', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);
      localStorage.setItem('user', JSON.stringify(data.user));
      localStorage.setItem('store', JSON.stringify(data.store));

      // セッション情報をセキュアに保管
      secureSetItem('authSession', {
        userId: data.user.userId,
        storeId: data.store.id
      });

      const duration = Date.now() - startTime;

      // ログイン成功をログ
      logger.userAction('login_success', {
        userId: data.user.userId,
        userName: data.user.name,
        storeId: data.store.id,
        storeName: data.store.name,
        role: data.user.role,
        timestamp: new Date().toISOString()
      });

      // パフォーマンスログ
      logger.performance('login', duration, {
        userId: data.user.userId,
        storeId: data.store.id
      });

      return { success: true };
    } catch (error) {
      const duration = Date.now() - startTime;
      const errorMessage = handleApiError(error);
      setError(errorMessage);

      // ログイン失敗をログ
      logger.userAction('login_failed', {
        userId,
        error: errorMessage,
        timestamp: new Date().toISOString()
      });

      // エラーログ
      logger.error('Login failed', {
        context: 'auth',
        userId,
        error: errorMessage,
        duration
      });

      logError(error, 'AuthContext.login');
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = useCallback(async (): Promise<void> => {
    // Prevent recursive logout calls
    if (isLoggingOut) {
      console.log('Logout already in progress, skipping duplicate call');
      return;
    }

    setIsLoggingOut(true);

    // ログアウト前のユーザー情報を保存
    const logoutUserId = currentUser?.userId;
    const logoutUserName = currentUser?.name;
    const logoutStoreId = currentStore?.id;
    const logoutStoreName = currentStore?.name;

    // ローカル状態を先にクリアして、UIが即座に更新されるようにする
    setCurrentUser(null);
    setCurrentStore(null);
    setToken(null);
    setRefreshToken(null);
    setError(null);

    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('store');
    localStorage.removeItem('authSession');

    // サーバー側のログアウト処理を非同期で実行（エラーを無視）
    try {
      await authApi.logout();

      // ログアウトをログ（成功）
      logger.userAction('logout', {
        userId: logoutUserId,
        userName: logoutUserName,
        storeId: logoutStoreId,
        storeName: logoutStoreName,
        timestamp: new Date().toISOString(),
        status: 'success'
      });
    } catch (error: any) {
      // 401エラーは既にトークンが無効なので正常とみなす
      if (error?.status === 401 || error?.response?.status === 401) {
        logger.userAction('logout', {
          userId: logoutUserId,
          userName: logoutUserName,
          storeId: logoutStoreId,
          storeName: logoutStoreName,
          timestamp: new Date().toISOString(),
          status: 'token_already_invalid'
        });
      } else if (error?.response?.status === 403) {
        logger.userAction('logout', {
          userId: logoutUserId,
          userName: logoutUserName,
          storeId: logoutStoreId,
          storeName: logoutStoreName,
          timestamp: new Date().toISOString(),
          status: 'already_logged_out'
        });
      } else {
        // その他のエラーはログを出力するが、ローカルログアウトは成功とみなす
        logger.error('AuthContext.logout API call failed', { error });
        logger.userAction('logout', {
          userId: logoutUserId,
          userName: logoutUserName,
          storeId: logoutStoreId,
          storeName: logoutStoreName,
          timestamp: new Date().toISOString(),
          status: 'failed_api_but_local_success'
        });
      }
    } finally {
      setIsLoggingOut(false);
    }
  }, [currentUser, currentStore, isLoggingOut]);

  const isAuthenticated = (): boolean => {
    return !!(currentUser && currentStore && token && refreshToken);
  };

  const hasRole = (role: string): boolean => {
    return currentUser?.role === role;
  };

  const isAdmin = (): boolean => {
    return hasRole('admin');
  };

  // Set up auto-logout listener after logout function is defined
  useEffect(() => {
    const handleAutoLogout = () => {
      logger.userAction('auto_logout', {
        reason: 'token_expired',
        timestamp: new Date().toISOString()
      });
      logout();
    };

    window.addEventListener('auth:logout', handleAutoLogout);

    return () => {
      window.removeEventListener('auth:logout', handleAutoLogout);
    };
  }, [logout]);

  const refreshTokens = async (): Promise<RefreshResponse> => {
    const startTime = Date.now();

    try {
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      logger.userAction('token_refresh_attempt', {
        userId: currentUser?.userId,
        timestamp: new Date().toISOString()
      });

      const data = await authApi.refresh(refreshToken);

      setCurrentUser(data.user);
      setCurrentStore(data.store);
      setToken(data.accessToken);
      setRefreshToken(data.refreshToken);

      localStorage.setItem('token', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);
      localStorage.setItem('user', JSON.stringify(data.user));
      localStorage.setItem('store', JSON.stringify(data.store));

      const duration = Date.now() - startTime;

      logger.userAction('token_refresh_success', {
        userId: data.user.userId,
        storeId: data.store.id,
        timestamp: new Date().toISOString()
      });

      logger.performance('token_refresh', duration, {
        userId: data.user.userId
      });

      return { success: true };
    } catch (error) {
      const duration = Date.now() - startTime;

      logger.userAction('token_refresh_failed', {
        userId: currentUser?.userId,
        error: error instanceof Error ? error.message : String(error),
        timestamp: new Date().toISOString()
      });

      logger.error('Token refresh failed', {
        context: 'auth',
        userId: currentUser?.userId,
        error: error instanceof Error ? error.message : String(error),
        duration
      });

      logError(error, 'AuthContext.refreshTokens');
      logout(); // If refresh fails, log out the user
      return { success: false, error: error instanceof Error ? error.message : String(error) };
    }
  };

  const setAuthFromSignup = useCallback(
    (signupData: { user: AuthUser; store: Store; access_token: string; refresh_token: string }) => {
      // 認証状態を設定
      setCurrentUser(signupData.user);
      setCurrentStore(signupData.store);
      setToken(signupData.access_token);
      setRefreshToken(signupData.refresh_token);

      // localStorageに保存（AuthContextの既存パターンと一貫性を保つ）
      localStorage.setItem('token', signupData.access_token);
      localStorage.setItem('refreshToken', signupData.refresh_token);
      localStorage.setItem('user', JSON.stringify(signupData.user));
      localStorage.setItem('store', JSON.stringify(signupData.store));

      // セッション情報をセキュアに保管
      secureSetItem('authSession', {
        userId: signupData.user.userId,
        storeId: signupData.store.id,
        loginTime: new Date().toISOString()
      });

      // サインアップ完了をログ
      logger.userAction('signup_auth_success', {
        userId: signupData.user.userId,
        userName: signupData.user.name,
        storeId: signupData.store.id,
        storeName: signupData.store.name,
        timestamp: new Date().toISOString()
      });
    },
    []
  );

  const refreshStoreInfo = useCallback(async (): Promise<void> => {
    if (!currentStore) {
      return;
    }

    try {
      // storeApi経由で最新の店舗情報を取得
      const { storeApi } = await import('../services/api/endpoints/store');
      const updatedStore = await storeApi.getStore(currentStore.id);

      // 状態とlocalStorageを更新
      setCurrentStore(updatedStore);
      localStorage.setItem('store', JSON.stringify(updatedStore));

      logger.userAction('store_info_refreshed', {
        storeId: updatedStore.id,
        storeName: updatedStore.name,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      logger.error('Failed to refresh store info', {
        context: 'auth',
        storeId: currentStore.id,
        error: error instanceof Error ? error.message : String(error)
      });
    }
  }, [currentStore]);

  const value: AuthContextType = {
    user: currentUser,
    store: currentStore,
    login,
    logout,
    isAuthenticated,
    loading: isLoading,
    setAuthFromSignup,
    refreshStoreInfo
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
